import { createTheme } from '@mui/material/styles';

const blueMain = '#5176d7';
const blueLight = '#4f7fff';
const blueDark = '#153077';
const goldenMain = '#DAA520';
const goldenLight = '#ffd56b';
const goldenDark = '#B7950B';
const textPrimary = '#bcd4eb';
const textSecondary = '#8c9bb5';

export const fantasyTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: blueMain, //Dark blue black
      light: blueLight,
      dark: blueDark,
    },
    secondary: {
      main: goldenMain, // More vibrant golden color
      light: goldenLight,
      dark: goldenDark,
    },
    background: {
      main: '#03060a',
      default: '#040a12',
      light: '#06090c',
      dark: '#020508',
    },
    error: {
      main: '#8B0000', // Deeper blood red
    },
    warning: {
      main: '#856109', // Aged bronze
    },
    success: {
      main: '#590269',
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
    },
    action: {
      selected: 'rgba(44, 62, 80, 0.3)',  // Darker blue with 20% opacity (was likely 0.08-0.12)
      hover: 'rgba(44, 62, 80, 0.2)'      // Keep hover state lighter
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'MedievalSharp',
          borderRadius: '8px',
          padding: '6px 12px',
          fontSize: '1.1rem',
          textTransform: 'none',
          fontWeight: 700,
          letterSpacing: '0.05em',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        },
        // contained + color="primary" = Gold/Yellow with 20% bg
        containedPrimary: {
          backgroundColor: 'rgba(183, 149, 11, 0.1)', // goldenDark at 20%
          color: goldenDark,
          border: 'none',
          '&:hover': {
            backgroundColor: 'rgba(218, 165, 32, 0.2)', // goldenMain at 30%
            color: goldenMain,
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: goldenDark,
            backgroundColor: 'rgba(183, 149, 11, 0.1)',
          },
        },
        // contained + color="secondary" = Blue with 20% bg
        containedSecondary: {
          backgroundColor: 'rgba(21, 48, 119, 0.1)', // blueMain at 20%
          color: blueMain,
          border: 'none',
          '&:hover': {
            backgroundColor: 'rgba(79, 127, 255, 0.2)', // blueLight at 30%
            color: blueLight,
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: blueMain,
            backgroundColor: 'rgba(21, 48, 119, 0.1)',
          },
        },
        // contained + color="error" = Red with 20% bg
        containedError: {
          backgroundColor: 'rgba(231, 76, 60, 0.1)',
          color: '#e74c3c',
          border: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 107, 107, 0.2)',
            color: '#ff6b6b',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
          },
        },
        // outlined + color="primary" = Gold/Yellow
        outlinedPrimary: {
          color: goldenDark,
          border: `2px solid ${goldenDark}`,
          '&:hover': {
            border: `2px solid ${goldenMain}`,
            color: goldenMain,
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: goldenDark,
            border: `2px solid ${goldenDark}`,
          },
        },
        // outlined + color="secondary" = Blue
        outlinedSecondary: {
          color: blueMain,
          border: `2px solid ${blueMain}`,
          '&:hover': {
            border: `2px solid ${blueLight}`,
            color: blueLight,
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: blueMain,
            border: `2px solid ${blueMain}`,
          },
        },
        // outlined + color="error" = Red
        outlinedError: {
          color: '#e74c3c',
          border: '2px solid #e74c3c',
          '&:hover': {
            border: '2px solid #ff6b6b',
            color: '#ff6b6b',
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            color: '#e74c3c',
            border: '2px solid #e74c3c',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#2C3E50',
          padding: 12,
          '&:hover': {
            backgroundColor: 'rgba(44, 62, 80, 0.1)',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.5,
            color: textSecondary,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.5,
            color: textSecondary,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          '&.Mui-disabled': {
            opacity: 0.5,
            color: textSecondary,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            height: '48px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            '& fieldset': {
              borderColor: textSecondary,
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: textPrimary,
            },
            '&.Mui-focused fieldset': {
              borderColor: blueLight,
              borderWidth: 2,
            },
            '& input': {
              color: textSecondary,
              fontFamily: 'MedievalSharp',
              '&::placeholder': {
                color: textSecondary,
                opacity: 1,
                fontFamily: 'MedievalSharp',
                fontWeight: 500
              }
            }
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontFamily: 'Merriweather',
            color: textSecondary,
            '&.Mui-focused': {
              color: blueLight,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'Merriweather',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: textSecondary,
            borderWidth: 2,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: textPrimary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: blueLight,
            borderWidth: 2,
          },
        },
        select: {
          color: textPrimary,
          fontFamily: 'Merriweather',
        },
        icon: {
          color: textSecondary,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#06090c',
          border: `2px solid ${textSecondary}`,
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
        list: {
          padding: '4px 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Merriweather',
          color: textPrimary,
          '&:hover': {
            backgroundColor: 'rgba(79, 127, 255, 0.15)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(79, 127, 255, 0.25)',
            '&:hover': {
              backgroundColor: 'rgba(79, 127, 255, 0.35)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Merriweather',
          borderRadius: '6px',
          '& .MuiChip-deleteIcon': {
            color: textSecondary,
            '&:hover': {
              color: textPrimary,
            },
          },
        },
        colorDefault: {
          backgroundColor: 'rgba(79, 127, 255, 0.2)',
          color: textPrimary,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          border: 'none',
          '&::before': {
            backgroundImage: `
              linear-gradient(
                90deg, 
                transparent 0%,
                rgba(140, 155, 181, 0.5) 30%,
                rgba(140, 155, 181, 0.5) 100%
              )
            `,
            height: '2px'
          },
          '&::after': {
            backgroundImage: `
              linear-gradient(
                -90deg, 
                transparent 0%,
                rgba(140, 155, 181, 0.5) 30%,
                rgba(140, 155, 181, 0.5) 100%
              )
            `,
            height: '2px'
          },
          '& .MuiDivider-wrapper': {
            color: textSecondary,
            fontFamily: 'MedievalSharp',
            fontSize: '1.1rem',
            padding: '0 48px',
            position: 'relative',
            '&::before, &::after': {
              content: '"❧"',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.2rem',
              color: textSecondary
            },
            '&::before': {
              left: '16px'
            },
            '&::after': {
              right: '16px'
            }
          }
        }
      }
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 0,
        "&:last-child": {
          paddingBottom: 0,
        },
      },
    },
  },
  typography: {
    fontFamily: 'Merriweather, serif',
    h1: {
      fontFamily: 'Cinzel Decorative',
      color: '#DAA520',
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5), 0 0 10px rgba(218, 165, 32, 0.3)',
      fontWeight: 700,
      letterSpacing: '0.05em',
    },
    h2: {
      fontFamily: 'Cinzel Decorative',
      color: '#DAA520',
      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.4)',
    },
    h3: {
      fontFamily: 'MedievalSharp',
      color: '#2C3E50',
      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
    },
    h4: {
      fontFamily: 'MedievalSharp',
      color: '#2C3E50',
    },
    h5: {
      fontFamily: 'MedievalSharp',
      color: '#2C3E50',
    },
    h6: {
      fontFamily: 'MedievalSharp',
      color: '#2C3E50',
    },
    body1: {
      fontFamily: 'Merriweather, serif',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
}); 

export const popoverStyles = (theme) => ({
  paper: {
    sx: {
      backgroundColor: theme.palette.background.light,
      border: `1px solid ${theme.palette.background.main}`,
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      marginTop: 3,
    }
  }
});